import React, { useEffect, useState } from 'react'
import { Button, Form, Checkbox } from 'semantic-ui-react'
import {
    FormTextFormField,
    FormTextAreaFormField,
    FormDropdownField,
    AppPrimaryButton,
    FormCheckBoxField,
} from 'elements'
import { AddEditSectionModal, SectionList } from 'page-components'
import {
    GetTemplateTypeDetails,
    GetTempleteDetails,
    GetPublicTempleteDetails,
    GetPublicTemplateTypeDetails,
} from 'hooks'
import {
    FlexColumn,
    FlexRow,
    FlexRowBetween,
    LoaderBar,
    PageHeader,
} from 'components'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom'
import {
    CommonUtility,
    ReorderType,
    TemplateService,
    AccessOptions,
    FormErrorText,
    APIPath,
    ChangeAction,
    formatFormulaSaveError,
    applyQuestionGuidPatches,
} from 'utility'
import { isNewUser } from 'utility/user-check'
import { canManageDepartmentsByRole } from 'utility/role-model'
import { AuthService } from 'utility/services/auth'
import { useAuth } from 'context/user'
import { useNotification } from 'hooks/toast'

const stampSectionsWithQuestionGuids = (sections, patches) => {
    if (!patches?.length) return sections
    let changed = false
    const next = (sections || []).map(section => {
        const questions = applyQuestionGuidPatches(section.Questions, patches)
        if (questions === section.Questions) return section
        changed = true
        return { ...section, Questions: questions }
    })
    return changed ? next : sections
}

const TemplateSchema = yup.object().shape({
    TemplateName: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .strict()
        .required('Template Name is required'),
    TemplateDescription: yup
        .string()
        .trim(FormErrorText.SpacesNotAllowed)
        .nullable()
        .required('Description is required'),
    TemplateType: yup
        .string()
        .notOneOf(["0", ""], "Type is required")
        .required("Type is required"),
    IsDefault: yup.boolean().nullable().default(false),
    IsLoginRequired: yup.boolean().nullable().default(false),
    AllowComment: yup.boolean().nullable().default(false),
    IsCapa: yup.boolean().nullable().default(false),
    IsSectionVisibleInDropdown: yup.boolean().nullable().default(false),
    IsShowHorizontal: yup.boolean().nullable().default(false),
    IsAutoRuleAllow: yup.boolean().nullable().default(false),
    BackgroundColorCode: yup.string().nullable(),
    TemplateAccess: yup.string().nullable().default(false),
})

const toColorInputValue = value => {
    const raw = String(value || '').trim()
    if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw
    if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
        return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`
    }
    return '#ffffff'
}

const BackgroundColorField = ({ control, disabled }) => (
    <Controller
        control={control}
        name="BackgroundColorCode"
        defaultValue="#ffffff"
        render={({ field }) => {
            const color = toColorInputValue(field.value)
            return (
                <Form.Field className="mb-3">
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                        }}
                    >
                        <label style={{ margin: 0 }}>Background</label>
                        <label
                            style={{
                                width: 44,
                                height: 28,
                                borderRadius: 6,
                                border: '1px solid #c5c9d1',
                                background: color,
                                position: 'relative',
                                overflow: 'hidden',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                flexShrink: 0,
                                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.35)',
                            }}
                        >
                            <input
                                type="color"
                                value={color}
                                disabled={disabled}
                                aria-label="Background"
                                onChange={event => field.onChange(event.target.value)}
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    opacity: 0,
                                    border: 0,
                                    padding: 0,
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                            />
                        </label>
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#344054',
                                letterSpacing: '0.03em',
                            }}
                        >
                            {color.toUpperCase()}
                        </span>
                    </div>
                </Form.Field>
            )
        }}
    />
)

export const AddEditTemplateScreen = () => {
    const navigate = useNavigate()
    const params = useParams()
    const location = useLocation();
    const { user } = useAuth()
    const { successToast, errorToast } = useNotification()
    const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId
    const [openModal, setOpenModal] = useState(false)
    const [currentSection, setCurrentSections] = useState(null)
    const [processing, setProcessing] = useState('')
    const [templateId, setTemplateId] = useState('')
    const [templateNumericId, setTemplateNumericId] = useState('')
    const [sectionEditMode, setSectionEditMode] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [templateName, setTemplateName] = useState('')

    const { data: template, loading } = GetTempleteDetails(params?.id)

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(TemplateSchema),
        defaultValues: {
            BackgroundColorCode: '#ffffff',
        },
    })
    const [templateData, setTemplateData] = useState({
        sections: [],
    })
    const stampTemplateQuestionGuids = patches => {
        setTemplateData(prev => {
            const sections = stampSectionsWithQuestionGuids(prev.sections, patches)
            if (sections === prev.sections) return prev
            return { ...prev, sections }
        })
    }
    const [printByTemplate, setPrintByTemplate] = useState(false)
    const [resetBySection, setResetBySection] = useState(false)
    const [showSectionSequenceNumber, setShowSectionSequenceNumber] =
        useState(false)
    const { data: templateTypes, loading: templateTypeLoading } =
        GetTemplateTypeDetails()
    const [responseTypes, setResponseTypes] = useState({
        MultipleResponse: [],
        SingleResponses: [],
        UserMultipleResponse: [],
    })

    useEffect(() => {
        if (template?.TemplateId) {
            reset({
                ...template,
                IsShowHorizontal: !!template.IsShowHorizontal,
                IsAutoRuleAllow: !!template.IsAutoRuleAllow,
                BackgroundColorCode: toColorInputValue(
                    template.BackgroundColorCode,
                ),
            })
            const normalizedSections = (template.Sections || []).map(section => {
                const canDelete =
                    typeof section?.CanDelete === 'boolean'
                        ? section.CanDelete
                        : !!section?.IsAllowDelete
                let isShowAlways
                if (typeof section?.IsShowAlways === 'boolean') {
                    isShowAlways = section.IsShowAlways
                } else if (section?.IsShowAlways !== undefined) {
                    isShowAlways = !!section?.IsShowAlways
                } else {
                    isShowAlways = true
                }
                return {
                    ...section,
                    CanDelete: canDelete,
                    IsShowAlways: isShowAlways,
                    IsNAAvailable: !!(
                        section?.IsNAAvailable ?? section?.isNAAvailable
                    ),
                }
            })
            setTemplateData({
                sections: normalizedSections,
            })
            setTemplateId(template.TemplateId)
            setTemplateNumericId(template.Id)

            const types = template.TemplateResponseType
            const groupedResponse = CommonUtility.groupByData(
                types.UserMultipleResponse || types.MultipleResponse,
                'GroupId',
            ).map(item => ({
                GroupId: item.title,
                Items: item.data,
            }))

            setResponseTypes({
                ...template.TemplateResponseType,
                GroupedResponse: groupedResponse,
            })
            // Prefill numbering flags if backend ever returns them
            setPrintByTemplate(!!template.PrintByTemplate)
            setResetBySection(!!template.ResetBySection)
        }
        if (template?.TemplateId && params.id) {
            setEditMode(true)
        }
    }, [template])

    // If coming from navigation with state, pre-fill templateId and templateName
    useEffect(() => {
        if (location.state && location.state.template) {
            const t = location.state.template;
            setTemplateId(t.TemplateId);
            setTemplateNumericId(t.Id || '');
            setTemplateName(t.TemplateName);
            reset({
                ...t,
                IsShowHorizontal: !!t.IsShowHorizontal,
                IsAutoRuleAllow: !!t.IsAutoRuleAllow,
                BackgroundColorCode: toColorInputValue(t.BackgroundColorCode),
            })
            setTemplateData({ sections: t.Sections || [] });
            if (t.TemplateResponseType) {
                const types = t.TemplateResponseType;
                const groupedResponse = CommonUtility.groupByData(
                    types.UserMultipleResponse || types.MultipleResponse,
                    'GroupId',
                ).map(item => ({
                    GroupId: item.title,
                    Items: item.data,
                }));
                setResponseTypes({
                    ...t.TemplateResponseType,
                    GroupedResponse: groupedResponse,
                });
            }
        }
    }, [location.state, reset]);

    const submit = async formData => {
        console.log('[Template Submit] ===== STARTING SUBMIT =====')
        console.log('[Template Submit] Edit mode:', editMode)
        console.log('[Template Submit] Template ID:', templateId)
        console.log('[Template Submit] Location state:', location.state)
        console.log('[Template Submit] Location state fromPublic:', location.state?.fromPublic)
        try {
            setProcessing('Saving')
            const templateObj = {
                ...formData,
                // If this edit originated from Public Library, always force IsCapa to false
                IsCapa: location.state?.fromPublic ? false : formData?.IsCapa,
                PrintByTemplate: !!printByTemplate,
                ResetBySection: !!resetBySection,
                IsSectionVisibleInDropdown: !!formData.IsSectionVisibleInDropdown,
                IsShowHorizontal: !!formData.IsShowHorizontal,
                IsAutoRuleAllow: !!formData.IsAutoRuleAllow,
                BackgroundColorCode: toColorInputValue(
                    formData.BackgroundColorCode,
                ),
                Sections: templateData.sections.map((ele, i) => {
                    const canDelete =
                        typeof ele?.CanDelete === 'boolean'
                            ? ele.CanDelete
                            : !!ele?.IsAllowDelete
                    let isShowAlways
                    if (typeof ele?.IsShowAlways === 'boolean') {
                        isShowAlways = ele.IsShowAlways
                    } else if (ele?.IsShowAlways !== undefined) {
                        isShowAlways = !!ele?.IsShowAlways
                    } else {
                        isShowAlways = true
                    }
                    const sectionBase = {
                        ...ele,
                        Order: i,
                        CanDelete: canDelete,
                        IsShowAlways: isShowAlways,
                        IsNAAvailable: !!(
                            ele?.IsNAAvailable ?? ele?.isNAAvailable
                        ),
                    }
                    delete sectionBase.IsAllowDelete
                    delete sectionBase.SectionURL
                    delete sectionBase.SectionUrl
                    // Ensure ChangeAction for sections (create/update/soft-delete)
                    if (typeof ele.ChangeAction === 'number') {
                        return {
                            ...sectionBase,
                            ChangeAction: ele.ChangeAction,
                            Questions: ele.Questions.map((q, idx) => {
                                const base = {
                                    ...q,
                                    DisplayOrder: idx,
                                    QuestionOrder: idx + 1,
                                    SectionURL:
                                        ele?.SectionURL ||
                                        ele?.SectionUrl ||
                                        q?.SectionURL ||
                                        q?.SectionUrl ||
                                        '',
                                }
                                // Ensure ChangeAction tagged for create/update/soft-delete
                                if (typeof q.ChangeAction === 'number') {
                                    return { ...base, ChangeAction: q.ChangeAction }
                                }
                                const isExisting = !!q?.OriginalId || !!q?.Id
                                return { ...base, ChangeAction: isExisting ? ChangeAction.None : ChangeAction.New }
                            }),
                        }
                    }
                    const isExistingSection = !!ele?.OriginalSectionId || !!ele?.SectionId
                    return {
                        ...sectionBase,
                        ChangeAction: isExistingSection ? ChangeAction.None : ChangeAction.New,
                        Questions: ele.Questions.map((q, idx) => {
                            const base = {
                                ...q,
                                DisplayOrder: idx,
                                QuestionOrder: idx + 1,
                                SectionURL:
                                    ele?.SectionURL ||
                                    ele?.SectionUrl ||
                                    q?.SectionURL ||
                                    q?.SectionUrl ||
                                    '',
                            }
                            // Ensure ChangeAction tagged for create/update/soft-delete
                            if (typeof q.ChangeAction === 'number') {
                                return { ...base, ChangeAction: q.ChangeAction }
                            }
                            const isExisting = !!q?.OriginalId || !!q?.Id
                            return { ...base, ChangeAction: isExisting ? ChangeAction.None : ChangeAction.New }
                        }),
                    }
                }),
            }
            const shouldCallUpdate = editMode
            const endpoint = shouldCallUpdate ? APIPath.templateUpdate : APIPath.template

            // Debug: one-time payload visibility for verification
            console.log('[Template Submit]', {
                endpoint,
                companyId,
                editMode,
                sectionsCount: templateObj.Sections?.length || 0,
                questionCounts: templateObj.Sections?.map(s => s.Questions?.length || 0),
                sectionsWithChangeAction: templateObj.Sections?.map(s => ({
                    SectionName: s.SectionName,
                    ChangeAction: s.ChangeAction,
                    QuestionChangeActions: s.Questions?.map(q => q.ChangeAction),
                })),
                payload: templateObj,
            })

            if (shouldCallUpdate) {
                // Send sections that have:
                // - ChangeAction.Delete (3) - deleted
                // - ChangeAction.New (1) - new section
                // - ChangeAction.Update (2) - section updated
                // - ChangeAction.SectionChange (4) - section metadata changed
                // - ChangeAction.SectionQuestionChange (5) - section has question changes
                // - Any questions with ChangeAction in [New, Update, Delete]
                const originalSections = (template?.Sections || [])
                // Helper function to find original section
                const findOriginalSection = (sec) => {
                    return originalSections.find(s =>
                        s.SectionId === sec.SectionId ||
                        s.SectionId === sec.OriginalSectionId ||
                        s.SectionId === sec.Id ||
                        (sec.SectionId && s.SectionId && s.SectionId.toString() === sec.SectionId.toString()),
                    )
                }
                const hasSectionMetaChange = (sec) => {
                    // Try to find original section
                    const orig = findOriginalSection(sec)
                    // If section doesn't exist in original, it's not a metadata change (it's new or doesn't exist)
                    if (!orig) {
                        return false
                    }
                    // Compare metadata fields - normalize boolean values for comparison
                    const origCanDelete = typeof orig?.CanDelete === 'boolean' ? orig.CanDelete : !!orig?.IsAllowDelete
                    const secCanDelete = typeof sec?.CanDelete === 'boolean' ? sec.CanDelete : !!sec?.IsAllowDelete
                    const canDeleteChanged = origCanDelete !== secCanDelete

                    // Compare IsShowAlways - normalize boolean values and handle default (true if undefined)
                    let origIsShowAlways
                    if (typeof orig?.IsShowAlways === 'boolean') {
                        origIsShowAlways = orig.IsShowAlways
                    } else if (orig?.IsShowAlways !== undefined) {
                        origIsShowAlways = !!orig.IsShowAlways
                    } else {
                        origIsShowAlways = true
                    }

                    let secIsShowAlways
                    if (typeof sec?.IsShowAlways === 'boolean') {
                        secIsShowAlways = sec.IsShowAlways
                    } else if (sec?.IsShowAlways !== undefined) {
                        secIsShowAlways = !!sec.IsShowAlways
                    } else {
                        secIsShowAlways = true
                    }
                    const isShowAlwaysChanged = origIsShowAlways !== secIsShowAlways

                    const origIsNAAvailable = !!(
                        orig?.IsNAAvailable ?? orig?.isNAAvailable
                    )
                    const secIsNAAvailable = !!(
                        sec?.IsNAAvailable ?? sec?.isNAAvailable
                    )
                    const isNAAvailableChanged =
                        origIsNAAvailable !== secIsNAAvailable

                    const sectionNameChanged = (orig?.SectionName || '').trim() !== (sec?.SectionName || '').trim()
                    const origSectionURL =
                        (orig?.SectionURL || orig?.SectionUrl || '').trim()
                    const secSectionURL =
                        (sec?.SectionURL || sec?.SectionUrl || '').trim()
                    const sectionURLChanged = origSectionURL !== secSectionURL
                    const hasChange =
                        canDeleteChanged ||
                        isShowAlwaysChanged ||
                        isNAAvailableChanged ||
                        sectionNameChanged ||
                        sectionURLChanged

                    return hasChange
                }

                const filteredSections = (templateObj.Sections || [])
                    .map(sec => {
                        const changedQs = (sec.Questions || []).filter(q =>
                            [ChangeAction.New, ChangeAction.Update, ChangeAction.Delete].includes(Number(q.ChangeAction)),
                        )

                        // If section is deleted, mark all questions as deleted
                        if (sec.ChangeAction === ChangeAction.Delete) {
                            return {
                                ...sec,
                                Questions: (sec.Questions || []).map(q => ({ ...q, ChangeAction: ChangeAction.Delete })),
                            }
                        }

                        // Determine section ChangeAction based on conditions
                        let sectionChangeAction = sec.ChangeAction
                        const hasMetaChange = hasSectionMetaChange(sec)
                        const hasQuestionChanges = changedQs.length > 0

                        // Check if section exists in original template to determine if it's new or edited
                        const origSection = findOriginalSection(sec)
                        // If hasMetaChange is true, section definitely exists in original (it's not new)
                        // Section is new only if: it doesn't exist in original AND (has ChangeAction.New OR has temporary ID)
                        const isNewSection = !hasMetaChange && !origSection && (sec.ChangeAction === ChangeAction.New || !sec.SectionId || (sec.SectionId && sec.SectionId.toString().includes('.')))

                        // If section is New, keep it as New
                        if (isNewSection || sec.ChangeAction === ChangeAction.New) {
                            sectionChangeAction = ChangeAction.New
                        } else if (sec.ChangeAction === ChangeAction.Delete) {
                            // Already handled above, but keep for safety
                            sectionChangeAction = ChangeAction.Delete
                        } else if (hasMetaChange && hasQuestionChanges) {
                            // Both metadata and questions changed - use SectionChange (4)
                            sectionChangeAction = ChangeAction.SectionChange
                        } else if (hasMetaChange) {
                            // Only metadata changed (SectionName, CanDelete, etc.)
                            sectionChangeAction = ChangeAction.SectionChange
                        } else if (hasQuestionChanges) {
                            // Only questions changed
                            sectionChangeAction = ChangeAction.SectionQuestionChange
                        } else if (sec.ChangeAction === ChangeAction.Update) {
                            // If section has Update (2), keep it
                            sectionChangeAction = ChangeAction.Update
                        } else {
                            // No changes detected, keep original ChangeAction (likely None)
                            sectionChangeAction = sec.ChangeAction || ChangeAction.None
                        }

                        // Include section if it has changes (not None)
                        if (sectionChangeAction !== ChangeAction.None) {
                            const sectionLevelUrl =
                                sec?.SectionURL ||
                                sec?.SectionUrl ||
                                ''
                            const questionsForPayload =
                                hasMetaChange && changedQs.length === 0
                                    ? (sec.Questions || []).map(q => ({
                                        ...q,
                                        ChangeAction:
                                            typeof q?.ChangeAction === 'number'
                                                ? q.ChangeAction
                                                : ChangeAction.None,
                                        SectionURL:
                                            sectionLevelUrl ||
                                            q?.SectionURL ||
                                            q?.SectionUrl ||
                                            '',
                                    }))
                                    : changedQs.map(q => ({
                                        ...q,
                                        SectionURL:
                                            sectionLevelUrl ||
                                            q?.SectionURL ||
                                            q?.SectionUrl ||
                                            '',
                                    }))

                            // Temporarily map SectionChange (4) to Update (2) for backward compatibility
                            const finalChangeAction = sectionChangeAction === ChangeAction.SectionChange
                                ? ChangeAction.Update
                                : sectionChangeAction

                            return {
                                ...(() => {
                                    const cleanedSection = { ...sec }
                                    delete cleanedSection.SectionURL
                                    delete cleanedSection.SectionUrl
                                    return cleanedSection
                                })(),
                                ChangeAction: finalChangeAction,
                                Questions: questionsForPayload,
                            }
                        }

                        return null
                    })
                    .filter(Boolean)

                // Debug: show what sections are being sent to API
                console.log('[Template Submit] Filtered sections being sent to API:', {
                    filteredSectionsCount: filteredSections.length,
                    filteredSections: filteredSections.map(s => ({
                        SectionName: s.SectionName,
                        ChangeAction: s.ChangeAction,
                        QuestionsCount: s.Questions?.length || 0,
                        IsDeletedSection: s.ChangeAction === ChangeAction.Delete,
                        QuestionChangeActions: s.Questions?.map(q => ({
                            QuestionId: q.QuestionId || q.Id,
                            ChangeAction: q.ChangeAction,
                        })) || [],
                    })),
                })

                const minimalUpdatePayload = {
                    ...templateObj,
                    Sections: filteredSections,
                }
                console.log('[Template Update - filtered]', minimalUpdatePayload)
                await TemplateService.updateTemplate(minimalUpdatePayload)

                const templateGuid = template?.TemplateId || templateId
                const renamedSections = (templateObj.Sections || []).filter(sec => {
                    if (Number(sec.ChangeAction) === ChangeAction.Delete) return false
                    if (sec.SectionId == null || sec.SectionId === '') return false
                    const orig = findOriginalSection(sec)
                    if (!orig) return false
                    const nextName = String(sec.SectionName || '').trim()
                    const prevName = String(orig.SectionName || '').trim()
                    return Boolean(nextName) && nextName !== prevName
                })
                await Promise.all(
                    renamedSections.map(sec =>
                        TemplateService.updateSectionName({
                            TemplateId: templateGuid,
                            SectionId: String(sec.SectionId),
                            SectionName: String(sec.SectionName || '').trim(),
                        }),
                    ),
                )

                successToast('Template updated successfully')
            } else {
                await TemplateService.create(templateObj)
            }

            // Complete the submit process (redirect logic)
            await completeSubmit()
        } catch (error) {
            console.log(error)
            const formulaError = formatFormulaSaveError(error)
            if (formulaError) errorToast(formulaError)
        } finally {
            setProcessing('')
        }
    }

    const addSection = () => {
        setCurrentSections({
            SectionId: Math.random(),
        })
        setSectionEditMode(false)
        setOpenModal(true)
    }

    const onClose = item => {
        setOpenModal(false)
        if (item) {
            if (sectionEditMode) {
                const temp = templateData.sections.map(x => {
                    if (x.SectionId === currentSection.SectionId) {
                        return { ...item }
                    }
                    return x
                })
                setTemplateData({
                    ...templateData,
                    sections: temp,
                })
            } else {
                const temp = [
                    ...templateData.sections,
                    {
                        ...item,
                        Order: templateData.sections.length + 1,
                    },
                ]
                setTemplateData({
                    ...templateData,
                    sections: temp,
                })
            }
        }
        setCurrentSections(null)
        setSectionEditMode(false)
    }

    const removeSection = item => {
        // Soft delete: mark ChangeAction=Delete for section AND all its questions
        setTemplateData({
            ...templateData,
            sections: templateData.sections.map((s, i) =>
                s.SectionId === item.SectionId
                    ? {
                        ...s,
                        ChangeAction: ChangeAction.Delete,
                        Order: i,
                        Questions: s.Questions.map(q => ({ ...q, ChangeAction: ChangeAction.Delete })),
                    }
                    : { ...s, Order: i },
            ),
        })
    }

    const completeSubmit = async () => {
        // Check if user is new (has no customers) and redirect to client setup
        // Only redirect to client setup if coming from public library or AI import (new user flows)
        const fromPublicLibrary = location.state?.fromPublic
        const fromAI = location.state?.fromAI
        console.log('[Template Submit] From public library:', fromPublicLibrary)
        console.log('[Template Submit] From AI:', fromAI)

        if (fromPublicLibrary || fromAI) {
            console.log('[Template Submit] Checking if user is new...')
            const userIsNew =
                canManageDepartmentsByRole(AuthService.getUser()) &&
                (await isNewUser())
            console.log('[Template Submit] User is new:', userIsNew)
            if (userIsNew) {
                console.log('[Template Submit] Redirecting to client setup...')
                navigate('/app/setup/client-setup')
            } else {
                console.log('[Template Submit] Redirecting to templates...')
                navigate('/app/templates')
            }
        } else {
            // Not from public library, just go to templates page
            console.log('[Template Submit] Not from public library, redirecting to templates...')
            navigate('/app/templates')
        }
    }

    const editSection = item => {
        setCurrentSections({ ...item })
        setSectionEditMode(true)
        setOpenModal(true)
    }
    const onDragCompleted = async list => {
        setTemplateData({
            ...templateData,
            sections: [...list.map((ele, i) => ({ ...ele, Order: i }))],
        })
        await TemplateService.sort(
            list.map((ele, i) => ({
                DisplayOrder: i,
                Id: ele.SectionId,
                OrderType: ReorderType.Section,
                TemplateId: template.TemplateId,
                SectionName: ele.SectionName,
            })),
        )
    }

    return (
        <div noValidate>
            <Form>
                {(processing || loading) && (
                    <LoaderBar content={processing || 'Loading'} />
                )}
                <div>
                    <PageHeader
                        header={`${editMode ? 'Edit' : 'Create'} Template`}
                    />
                </div>
                <div className="my-2">
                    <Form.Group widths="equal">
                        <FormTextFormField
                            control={control}
                            label="Template Name"
                            name="TemplateName"
                            placeholder="Template Name"
                            errors={errors?.TemplateName}
                            defaultValue={templateName}
                            onChange={setTemplateName}
                            required
                        />
                        <FormDropdownField
                            options={templateTypes}
                            loading={templateTypeLoading}
                            control={control}
                            label="Template Type"
                            name="TemplateType"
                            placeholder="Select Template Type"
                            errors={errors?.TemplateType}
                            defaultValue=""
                            disabled={!templateId}
                            required
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormTextAreaFormField
                            control={control}
                            label="Description"
                            name="TemplateDescription"
                            placeholder="Description"
                            errors={errors?.TemplateDescription}
                            defaultValue=""
                            widths={16}
                            disabled={!templateId}
                            required
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            label="Make Template"
                            name="TemplateAccess"
                            placeholder="Select template access"
                            options={AccessOptions.map(option => ({
                                key: option.Id,
                                text: option.name,
                                value: option.Id,
                            }))}
                            defaultValue={template?.TemplateAccess || 'Internal'}
                            disabled={!templateId}
                        />
                        <Form.Field>
                            <FlexColumn>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsDefault"
                                        label="IsDefault"
                                        disabled={!templateId}
                                    />
                                </Form.Field>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="PrintByTemplate"
                                        label="Print number on template"
                                        defaultValue={printByTemplate}
                                        disabled={!templateId}
                                        onChange={checked => setPrintByTemplate(!!checked)}
                                    />
                                </Form.Field>
                                {printByTemplate && (
                                    <Form.Field className="mb-3">
                                        <FormCheckBoxField
                                            control={control}
                                            name="ResetBySection"
                                            label="Reset by sections"
                                            defaultValue={resetBySection}
                                            disabled={!templateId}
                                            onChange={checked =>
                                                setResetBySection(!!checked)
                                            }
                                        />
                                    </Form.Field>
                                )}
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsSectionVisibleInDropdown"
                                        label="Show sections in dropdown"
                                        defaultValue={!!template?.IsSectionVisibleInDropdown}
                                        disabled={!templateId}
                                    />
                                </Form.Field>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsShowHorizontal"
                                        label="Show horizontal"
                                        defaultValue={!!template?.IsShowHorizontal}
                                        disabled={!templateId}
                                    />
                                </Form.Field>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsAutoRuleAllow"
                                        label="Allow Auto Rule"
                                        defaultValue={!!template?.IsAutoRuleAllow}
                                        disabled={!templateId}
                                    />
                                </Form.Field>
                                <BackgroundColorField
                                    control={control}
                                    disabled={!templateId}
                                />
                            </FlexColumn>
                        </Form.Field>
                    </Form.Group>
                </div>
                <div className="my-4">
                    <FlexRowBetween>
                        <PageHeader header="Section List" />
                        <FlexRow className="align-items-center">
                            <div className="d-flex align-items-center me-3">
                                <span className="me-2">Show sequence number</span>
                                <Checkbox
                                    toggle
                                    checked={showSectionSequenceNumber}
                                    onChange={(_, { checked }) =>
                                        setShowSectionSequenceNumber(!!checked)
                                    }
                                    disabled={!templateId}
                                />
                            </div>
                            <AppPrimaryButton
                                type="button"
                                onClick={addSection}
                                disabled={!templateId}
                            >
                                Create Section
                            </AppPrimaryButton>
                        </FlexRow>
                    </FlexRowBetween>
                    <SectionList
                        sectionList={templateData.sections.filter(s => s.ChangeAction !== ChangeAction.Delete)}
                        edit={editSection}
                        remove={removeSection}
                        onDragCompleted={onDragCompleted}
                        disabled={!templateId}
                        showNumbers={showSectionSequenceNumber}
                        resetBySection={!!resetBySection}
                    />
                </div>
                <FlexRow className="justify-content-end mb-5">
                    <AppPrimaryButton onClick={handleSubmit(submit)} disabled={!templateId}>
                        Submit
                    </AppPrimaryButton>
                    <Link to="/app/templates">
                        <Button type="button">Cancel</Button>
                    </Link>
                </FlexRow>
                {templateId && (
                    <AddEditSectionModal
                        open={openModal}
                        onClose={onClose}
                        editMode={sectionEditMode}
                        section={currentSection}
                        templateId={templateId}
                        templateNumericId={templateNumericId}
                        responseTypes={responseTypes}
                        setResponseTypes={setResponseTypes}
                        template={template}
                        resetBySection={!!resetBySection}
                        templateSections={templateData.sections}
                        onStampQuestionGuids={stampTemplateQuestionGuids}
                        startNumber={(() => {
                            if (!printByTemplate || resetBySection || !currentSection) return 1
                            const sections = templateData.sections || []
                            const idx = sections.findIndex(s => s.SectionId === currentSection.SectionId)
                            if (idx <= 0) return 1
                            let offset = 0
                            for (let i = 0; i < idx; i += 1) {
                                const q = Array.isArray(sections[i].Questions) ? sections[i].Questions : []
                                offset += q.length
                            }
                            return offset + 1
                        })()}
                    />
                )}
            </Form>
        </div>
    )
}

export const AddPublicEditTemplateScreen = () => {
    const navigate = useNavigate()
    const params = useParams()
    const { user } = useAuth();
    const { errorToast } = useNotification()
    const [openModal, setOpenModal] = useState(false)
    const [currentSection, setCurrentSections] = useState(null)
    const [processing, setProcessing] = useState('')
    const [templateId, setTemplateId] = useState('')
    const [templateNumericId, setTemplateNumericId] = useState('')
    const [sectionEditMode, setSectionEditMode] = useState(false)
    const [editMode, setEditMode] = useState(false)

    const { data: template, loading } = GetPublicTempleteDetails(params?.id, { IsAddToLibrary: true })

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(TemplateSchema),
        defaultValues: {
            BackgroundColorCode: '#ffffff',
        },
    })

    const [templateData, setTemplateData] = useState({
        sections: [],
    })
    const stampTemplateQuestionGuids = patches => {
        setTemplateData(prev => {
            const sections = stampSectionsWithQuestionGuids(prev.sections, patches)
            if (sections === prev.sections) return prev
            return { ...prev, sections }
        })
    }
    const { data: templateTypes, loading: templateTypeLoading } =
        GetPublicTemplateTypeDetails()
    const [responseTypes, setResponseTypes] = useState({
        MultipleResponse: [],
        SingleResponses: [],
        UserMultipleResponse: [],
    })
    const [printByTemplate, setPrintByTemplate] = useState(false)
    const [resetBySection, setResetBySection] = useState(false)

    useEffect(() => {
        if (template?.TemplateId) {
            reset({
                ...template,
                // Force IsCapa to false in the UI state
                IsCapa: false,
                IsShowHorizontal: !!template.IsShowHorizontal,
                IsAutoRuleAllow: !!template.IsAutoRuleAllow,
                BackgroundColorCode: toColorInputValue(
                    template.BackgroundColorCode,
                ),
            })
            const normalizedSections = (template.Sections || []).map(section => {
                const canDelete =
                    typeof section?.CanDelete === 'boolean'
                        ? section.CanDelete
                        : !!section?.IsAllowDelete
                let isShowAlways
                if (typeof section?.IsShowAlways === 'boolean') {
                    isShowAlways = section.IsShowAlways
                } else if (section?.IsShowAlways !== undefined) {
                    isShowAlways = !!section?.IsShowAlways
                } else {
                    isShowAlways = true
                }
                return {
                    ...section,
                    CanDelete: canDelete,
                    IsShowAlways: isShowAlways,
                    IsNAAvailable: !!(
                        section?.IsNAAvailable ?? section?.isNAAvailable
                    ),
                }
            })
            setTemplateData({
                sections: normalizedSections,
            })
            setTemplateId(template.TemplateId)
            setTemplateNumericId(template.Id)

            const types = template.TemplateResponseType
            const groupedResponse = CommonUtility.groupByData(
                types.UserMultipleResponse || types.MultipleResponse,
                'GroupId',
            ).map(item => ({
                GroupId: item.title,
                Items: item.data,
            }))

            setResponseTypes({
                ...template.TemplateResponseType,
                GroupedResponse: groupedResponse,
            })

            // Prefill numbering flags if backend returns them
            setPrintByTemplate(!!template.PrintByTemplate)
            setResetBySection(!!template.ResetBySection)
        }
        if (template?.TemplateId && params.id) {
            setEditMode(true)
        }
    }, [template])

    const submit = async formData => {
        try {
            setProcessing('Saving')

            // Prepare the template object
            const templateObj = {
                ...formData,
                TemplateId: templateId, // Include the TemplateId from GET response for public template addition
                IsCapa: false, // Always false when adding from library
                PrintByTemplate: !!printByTemplate,
                ResetBySection: !!resetBySection,
                IsSectionVisibleInDropdown: !!formData.IsSectionVisibleInDropdown,
                IsShowHorizontal: !!formData.IsShowHorizontal,
                IsAutoRuleAllow: !!formData.IsAutoRuleAllow,
                BackgroundColorCode: toColorInputValue(
                    formData.BackgroundColorCode,
                ),
                Sections: templateData.sections.map((ele, i) => {
                    const canDelete =
                        typeof ele?.CanDelete === 'boolean'
                            ? ele.CanDelete
                            : !!ele?.IsAllowDelete
                    let isShowAlways
                    if (typeof ele?.IsShowAlways === 'boolean') {
                        isShowAlways = ele.IsShowAlways
                    } else if (ele?.IsShowAlways !== undefined) {
                        isShowAlways = !!ele?.IsShowAlways
                    } else {
                        isShowAlways = true
                    }
                    const sectionBase = {
                        ...ele,
                        Order: i,
                        CanDelete: canDelete,
                        IsShowAlways: isShowAlways,
                        IsNAAvailable: !!(
                            ele?.IsNAAvailable ?? ele?.isNAAvailable
                        ),
                    }
                    delete sectionBase.IsAllowDelete
                    delete sectionBase.SectionURL
                    delete sectionBase.SectionUrl
                    // Ensure ChangeAction for sections (create/update/soft-delete)
                    if (typeof ele.ChangeAction === 'number') {
                        return {
                            ...sectionBase,
                            ChangeAction: ele.ChangeAction,
                            Questions: ele.Questions.map((q, idx) => ({
                                ...q,
                                DisplayOrder: idx,
                                QuestionOrder: idx + 1,
                                SectionURL:
                                    ele?.SectionURL ||
                                    ele?.SectionUrl ||
                                    q?.SectionURL ||
                                    q?.SectionUrl ||
                                    '',
                            })),
                        }
                    }
                    const isExistingSection = !!ele?.OriginalSectionId || !!ele?.SectionId
                    return {
                        ...sectionBase,
                        ChangeAction: isExistingSection ? ChangeAction.None : ChangeAction.New,
                        Questions: ele.Questions.map((q, idx) => ({
                            ...q,
                            DisplayOrder: idx,
                            QuestionOrder: idx + 1,
                            SectionURL:
                                ele?.SectionURL ||
                                ele?.SectionUrl ||
                                q?.SectionURL ||
                                q?.SectionUrl ||
                                '',
                        })),
                    }
                }),
                CreatedBy: user?.UserId, // Set the current user as creator
            };

            // Do NOT delete TemplateId for public template addition flow
            // delete templateObj.TemplateId; // This line is removed

            await TemplateService.create(templateObj);

            navigate('/app/templates');
        } catch (error) {
            console.log(error);
            const formulaError = formatFormulaSaveError(error)
            if (formulaError) errorToast(formulaError)
        } finally {
            setProcessing('');
        }
    }

    const addSection = () => {
        setCurrentSections({
            SectionId: Math.random(),
        })
        setSectionEditMode(false)
        setOpenModal(true)
    }

    const onClose = item => {
        setOpenModal(false)
        if (item) {
            if (sectionEditMode) {
                const temp = templateData.sections.map(x => {
                    if (x.SectionId === currentSection.SectionId) {
                        return { ...item }
                    }
                    return x
                })
                setTemplateData({
                    ...templateData,
                    sections: temp,
                })
            } else {
                const temp = [
                    ...templateData.sections,
                    {
                        ...item,
                        Order: templateData.sections.length + 1,
                    },
                ]
                setTemplateData({
                    ...templateData,
                    sections: temp,
                })
            }
        }
        setCurrentSections(null)
        setSectionEditMode(false)
    }

    const removeSection = item => {
        // Soft delete: mark ChangeAction=Delete for section AND all its questions
        setTemplateData({
            ...templateData,
            sections: templateData.sections.map((s, i) =>
                s.SectionId === item.SectionId
                    ? {
                        ...s,
                        ChangeAction: ChangeAction.Delete,
                        Order: i,
                        Questions: s.Questions.map(q => ({ ...q, ChangeAction: ChangeAction.Delete })),
                    }
                    : { ...s, Order: i },
            ),
        })
    }

    const editSection = item => {
        setCurrentSections({ ...item })
        setSectionEditMode(true)
        setOpenModal(true)
    }
    const onDragCompleted = async list => {
        setTemplateData({
            ...templateData,
            sections: [...list.map((ele, i) => ({ ...ele, Order: i }))],
        })
        await TemplateService.sort(
            list.map((ele, i) => ({
                DisplayOrder: i,
                Id: ele.SectionId,
                OrderType: ReorderType.Section,
                TemplateId: template.TemplateId,
                SectionName: ele.SectionName,
            })),
        )
    }
    return (
        <div noValidate>
            <Form>
                {(processing || loading) && (
                    <LoaderBar content={processing || 'Loading'} />
                )}
                <div>
                    <PageHeader
                        header={`${editMode ? 'Edit' : 'Create'} Template`}
                    />
                </div>
                <div className="my-2">
                    <Form.Group widths="equal">
                        <FormTextFormField
                            control={control}
                            label="Template Name"
                            name="TemplateName"
                            placeholder="Template Name"
                            errors={errors?.TemplateName}
                            defaultValue=""
                            required
                        />
                        <FormDropdownField
                            options={templateTypes}
                            loading={templateTypeLoading}
                            control={control}
                            label="Template Type"
                            name="TemplateType"
                            placeholder="Select Template Type"
                            errors={errors?.TemplateType}
                            defaultValue=""
                            required
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormTextAreaFormField
                            control={control}
                            label="Description"
                            name="TemplateDescription"
                            placeholder="Description"
                            errors={errors?.TemplateDescription}
                            defaultValue=""
                            widths={16}
                            required
                        />
                    </Form.Group>
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            label="Make Template"
                            name="TemplateAccess"
                            placeholder="Select template access"
                            options={AccessOptions.map(option => ({
                                key: option.Id,
                                text: option.name,
                                value: option.Id,
                            }))}
                            defaultValue={template?.TemplateAccess || 'Internal'}
                        />
                        <Form.Field>
                            <FlexColumn>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsDefault"
                                        label="IsDefault"
                                    />
                                </Form.Field>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="PrintByTemplate"
                                        label="Print number on template"
                                        defaultValue={printByTemplate}
                                        onChange={checked => setPrintByTemplate(!!checked)}
                                    />
                                </Form.Field>
                                {printByTemplate && (
                                    <Form.Field className="mb-3">
                                        <FormCheckBoxField
                                            control={control}
                                            name="ResetBySection"
                                            label="Reset by sections"
                                            defaultValue={resetBySection}
                                            onChange={checked =>
                                                setResetBySection(!!checked)
                                            }
                                        />
                                    </Form.Field>
                                )}
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsSectionVisibleInDropdown"
                                        label="Show sections in dropdown"
                                        defaultValue={
                                            !!template?.IsSectionVisibleInDropdown
                                        }
                                    />
                                </Form.Field>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsShowHorizontal"
                                        label="Show horizontal"
                                        defaultValue={!!template?.IsShowHorizontal}
                                    />
                                </Form.Field>
                                <Form.Field className="mb-3">
                                    <FormCheckBoxField
                                        control={control}
                                        name="IsAutoRuleAllow"
                                        label="Allow Auto Rule"
                                        defaultValue={!!template?.IsAutoRuleAllow}
                                    />
                                </Form.Field>
                                <BackgroundColorField control={control} />
                            </FlexColumn>
                        </Form.Field>
                    </Form.Group>
                </div>
                <div className="my-4">
                    <FlexRowBetween>
                        <PageHeader header="Section List" />
                        <span>
                            <AppPrimaryButton
                                type="button"
                                onClick={addSection}
                                disabled={!templateId}
                            >
                                Create Section
                            </AppPrimaryButton>
                        </span>
                    </FlexRowBetween>
                    <SectionList
                        sectionList={templateData.sections.filter(s => s.ChangeAction !== ChangeAction.Delete)}
                        edit={editSection}
                        remove={removeSection}
                        onDragCompleted={onDragCompleted}
                        disabled={!templateId}
                    />
                </div>
                <FlexRow className="justify-content-end mb-5">
                    <AppPrimaryButton onClick={handleSubmit(submit)} disabled={!templateId}>
                        Submit
                    </AppPrimaryButton>
                    <Link to="/app/templates">
                        <Button type="button">Cancel</Button>
                    </Link>
                </FlexRow>
                {templateId && (
                    <AddEditSectionModal
                        open={openModal}
                        onClose={onClose}
                        editMode={sectionEditMode}
                        section={currentSection}
                        templateId={templateId}
                        templateNumericId={templateNumericId}
                        responseTypes={responseTypes}
                        setResponseTypes={setResponseTypes}
                        template={template}
                        templateSections={templateData.sections}
                        onStampQuestionGuids={stampTemplateQuestionGuids}
                    />
                )}
                {console.log('Passing templateId to AddEditSectionModal:', templateId)}
            </Form>
        </div>
    )
}
